import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import toast from 'react-hot-toast';

const FollowUpDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [followUp, setFollowUp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [patientCondition, setPatientCondition] = useState('good');
  const [complications, setComplications] = useState('');
  const [recommendations, setRecommendations] = useState('');

  useEffect(() => {
    fetchFollowUp();
  }, [id]);

  const fetchFollowUp = async () => {
    try {
      const response = await api.get(`/follow-ups/${id}`);
      setFollowUp(response.data.data);
    } catch (error) {
      toast.error('Failed to load follow-up');
      navigate('/follow-ups');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setUpdating(true);
    try {
      await api.post(`/follow-ups/${id}/complete`, {
        feedback,
        patientCondition,
        complications,
        recommendations
      });
      toast.success('Follow-up completed successfully!');
      setShowCompleteModal(false);
      setFeedback('');
      setPatientCondition('good');
      setComplications('');
      setRecommendations('');
      fetchFollowUp();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete follow-up');
    } finally {
      setUpdating(false);
    }
  };

  const handleMarkMissed = async () => {
    if (!window.confirm('Are you sure you want to mark this follow-up as missed?')) {
      return;
    }

    setUpdating(true);
    try {
      await api.post(`/follow-ups/${id}/missed`, { reason: 'Patient not available' });
      toast.success('Follow-up marked as missed!');
      fetchFollowUp();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to mark follow-up as missed');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      scheduled: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      missed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getConditionColor = (condition) => {
    const colors = {
      excellent: 'bg-green-100 text-green-800',
      good: 'bg-blue-100 text-blue-800',
      fair: 'bg-yellow-100 text-yellow-800',
      poor: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return colors[condition] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!followUp) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Follow-up not found</p>
        <Link to="/follow-ups" className="text-primary-600 hover:text-primary-700">
          Back to follow-ups
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/follow-ups')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            ←
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Follow-up Details</h1>
        </div>
        <div className="flex space-x-2">
          {(user?.role === 'customer_care' || user?.role === 'cc_head') && (followUp.status === 'pending' || followUp.status === 'scheduled') && (
            <>
              <button
                onClick={handleMarkMissed}
                disabled={updating}
                className="px-4 py-2 border border-red-600 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
              >
                Mark Missed
              </button>
              <button
                onClick={() => setShowCompleteModal(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Complete
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Follow-up Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Follow-up Number</p>
                <p className="font-medium">#{followUp.followUpNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Type</p>
                <p className="font-medium capitalize">{followUp.followUpType}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Scheduled Date</p>
                <p className="font-medium">{new Date(followUp.scheduledDate).toLocaleDateString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Scheduled Time</p>
                <p className="font-medium">{followUp.scheduledTime}</p>
              </div>
            </div>
          </div>

          {followUp.feedback && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Feedback</h2>
              <p className="text-gray-700">{followUp.feedback}</p>
            </div>
          )}

          {followUp.complications && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Complications</h2>
              <p className="text-gray-700">{followUp.complications}</p>
            </div>
          )}

          {followUp.recommendations && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recommendations</h2>
              <p className="text-gray-700">{followUp.recommendations}</p>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Status</h2>
            <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(followUp.status)}`}>
              {followUp.status}
            </span>
            {followUp.patientCondition && (
              <div className="mt-4">
                <p className="text-sm text-gray-600">Patient Condition</p>
                <span className={`px-2 py-1 text-xs rounded-full ${getConditionColor(followUp.patientCondition)}`}>
                  {followUp.patientCondition}
                </span>
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Patient</h2>
            <p className="font-medium">{followUp.patientName}</p>
            <p className="text-sm text-gray-600">{followUp.patientPhone}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Doctor</h2>
            <p className="font-medium">
              Dr. {followUp.doctor?.user?.firstName} {followUp.doctor?.user?.lastName}
            </p>
            <p className="text-sm text-gray-600">{followUp.doctor?.specialization}</p>
          </div>

          {followUp.assignedTo && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Assigned To</h2>
              <p className="font-medium">
                {followUp.assignedTo?.firstName} {followUp.assignedTo?.lastName}
              </p>
            </div>
          )}

          {followUp.completedAt && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Completion</h2>
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Completed At</p>
                  <p className="font-medium">{new Date(followUp.completedAt).toLocaleString()}</p>
                </div>
                {followUp.completedBy && (
                  <div>
                    <p className="text-sm text-gray-600">Completed By</p>
                    <p className="font-medium">
                      {followUp.completedBy?.firstName} {followUp.completedBy?.lastName}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {followUp.nextFollowUpDate && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Next Follow-up</h2>
              <p className="font-medium">{new Date(followUp.nextFollowUpDate).toLocaleDateString()}</p>
              {followUp.nextFollowUpNotes && (
                <p className="text-sm text-gray-600 mt-2">{followUp.nextFollowUpNotes}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Complete Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">Complete Follow-up</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Patient Condition</label>
                <select
                  value={patientCondition}
                  onChange={(e) => setPatientCondition(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="excellent">Excellent</option>
                  <option value="good">Good</option>
                  <option value="fair">Fair</option>
                  <option value="poor">Poor</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Feedback</label>
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Complications</label>
                <textarea
                  value={complications}
                  onChange={(e) => setComplications(e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recommendations</label>
                <textarea
                  value={recommendations}
                  onChange={(e) => setRecommendations(e.target.value)}
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6">
              <button
                onClick={() => setShowCompleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleComplete}
                disabled={updating}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {updating ? 'Completing...' : 'Complete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUpDetail;
